/**
 * Калькулятор практика (CalcFork): те же два режима, что в его окне.
 * Деньги — Decimal, не float. Расклад банка в «Плане» — `stakes()` сканера,
 * чтобы в купон ушло то же, что на столе. «Вилка %» и добор — как у него:
 * край (1 − S)·100 и доплата к победившему плечу.
 */

import { Decimal } from 'decimal.js';

import { impliedSum, stakes } from './calc';

export type ForkKind = 'odds' | 'prob';
export type ForkMode = 'total' | 'placed';
export type ForkCur = 'USD' | 'RUB';

function parseNum(raw: string): Decimal | null {
  const text = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (text === '' || text === '.') return null;
  try {
    const n = new Decimal(text);
    return n.isFinite() ? n : null;
  } catch {
    return null;
  }
}

/** Кэф из поля: число кэфа или 100/%. */
export function quoteToOdds(value: string, kind: ForkKind): Decimal | null {
  const n = parseNum(value);
  if (n === null) return null;
  if (kind === 'odds') return n.gt(1) ? n : null;
  if (n.gt(0) && n.lt(100)) return new Decimal(100).div(n);
  return null;
}

/** Вероятность 0…1 из поля. */
export function quoteToProb(value: string, kind: ForkKind): Decimal | null {
  const n = parseNum(value);
  if (n === null) return null;
  if (kind === 'odds') return n.gt(1) ? new Decimal(1).div(n) : null;
  if (n.gt(0) && n.lt(100)) return n.div(100);
  return null;
}

export function oddsToProbPct(odds: Decimal): Decimal {
  return new Decimal(100).div(odds);
}

/** (1 − S) · 100 — «Вилка %» в окне практика, не ROI сканера. */
export function forkEdgePct(odds: readonly Decimal[]): Decimal | null {
  if (odds.length < 2 || odds.some((one) => !one.gt(1))) return null;
  const sum = impliedSum([...odds]);
  return new Decimal(1).minus(sum).times(100);
}

export function moneyText(n: Decimal): string {
  return n.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2).replace('.', ',');
}

export function pctText(n: Decimal): string {
  const abs = n.abs();
  const body = abs.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2).replace('.', ',');
  if (n.gt(0)) return `+${body}%`;
  if (n.lt(0)) return `−${body}%`;
  return `${body}%`;
}

export function toDisplay(
  amount: Decimal,
  from: ForkCur,
  to: ForkCur,
  fx: Decimal,
): Decimal | null {
  if (from === to) return amount;
  if (!fx.gt(0)) return null;
  return from === 'USD' ? amount.times(fx) : amount.div(fx);
}

export type PlanResult = {
  edge: Decimal;
  stake1: Decimal;
  stake2: Decimal;
  profit: Decimal;
};

/** Расклад банка — тот же `stakes()`, что стол. Прибыль = банк · (1/S − 1). */
export function planFork(bank: string, odds1: Decimal, odds2: Decimal): PlanResult | null {
  const S = parseNum(bank);
  if (S === null || !S.gt(0) || !odds1.gt(1) || !odds2.gt(1)) return null;
  const edge = forkEdgePct([odds1, odds2]);
  if (edge === null) return null;
  const [st1, st2] = stakes(S, [odds1, odds2]);
  if (st1 === undefined || st2 === undefined) return null;
  const sum = impliedSum([odds1, odds2]);
  if (!sum.gt(0)) return null;
  const profit = S.times(new Decimal(1).div(sum).minus(1));
  return { edge, stake1: st1, stake2: st2, profit };
}

export type PlacedResult = {
  edge: Decimal;
  pnl: Decimal;
  topUp: Decimal | null;
};

/**
 * Чистое, если выиграло выбранное плечо: sW·(kW − 1) − sL.
 * В минусе — сколько докинуть на это плечо, чтобы выйти в ноль с запасом 0.01.
 */
export function placedFork(
  winStake: Decimal,
  winOdds: Decimal,
  loseStake: Decimal,
  loseOdds: Decimal,
): PlacedResult | null {
  if (!winStake.gt(0) || !loseStake.gt(0) || !winOdds.gt(1) || !loseOdds.gt(1)) return null;
  const edge = forkEdgePct([winOdds, loseOdds]);
  if (edge === null) return null;
  const pnl = winStake.times(winOdds.minus(1)).minus(loseStake);
  let topUp: Decimal | null = null;
  if (pnl.lt(0)) {
    const need = loseStake.plus('0.01').div(winOdds.minus(1)).minus(winStake);
    if (need.gt(0)) topUp = need.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  }
  return { edge, pnl: pnl.toDecimalPlaces(2, Decimal.ROUND_HALF_UP), topUp };
}

/** Точка с любой раскладки: ю, /, запятая. */
export function sanitizeForkNum(value: string): string {
  let next = value.replace(/[юЮ/]/g, '.');
  next = next.replace(/[^0-9.,]/g, '');
  const parts = next.split(/[.,]/);
  if (parts.length > 2) next = `${parts[0]}.${parts.slice(1).join('')}`;
  return next;
}
